// === Shared SDS / Chemical Label document viewer ===
// THE single hook + viewer used across every screen (admin chemicals,
// fertilizers, safety-data-sheets; web settings lists, spray-task & pest-disease
// dialogs) for opening Safety Data Sheet and Chemical Label documents.
//
// The links themselves resolve server-side from the safety_data_sheets FK (single
// source of truth) — this module is purely the consistent open / preview / download
// behaviour. Feed it an already-resolved URL via openViewer(url, title).
//
// Usage:
//   const { viewer, openViewer, closeViewer } = useSdsDocuments();
//   <button onClick={() => openViewer(row.safety_data_sheet_link_url, 'SDS')} />
//   <SdsDocumentViewerDialog viewer={viewer} onClose={closeViewer} />

import { useState, type JSX } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SdsViewerState {
  readonly open: boolean;
  readonly url: string | null;
  readonly title: string;
}

export interface UseSdsDocumentsReturn {
  /** Open a resolved URL/PDF in the inline viewer dialog */
  readonly openViewer: (url: string | null | undefined, title?: string) => void;
  /** Close the inline viewer dialog */
  readonly closeViewer: () => void;
  /** Current viewer state — pass to <SdsDocumentViewerDialog> */
  readonly viewer: SdsViewerState;
  /** Open a URL in a new browser tab */
  readonly openLink: (url: string) => void;
  /** Trigger a file download (falls back to a new tab on failure) */
  readonly downloadLink: (url: string, filename?: string) => Promise<void>;
}

// ─── Standalone utilities (no React) ─────────────────────────────────────────

/** Open a URL in a new tab — works for PDFs, web URLs, and signed storage URLs */
export function openLink(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function extractFilename(url: string): string {
  try {
    const { pathname } = new URL(url);
    const parts = pathname.split('/');
    return decodeURIComponent(parts[parts.length - 1] ?? 'download');
  } catch {
    return 'download';
  }
}

/**
 * Trigger a file download. Uses fetch + Blob URL so the browser saves the file
 * rather than displaying it. Falls back to openLink if the fetch fails.
 */
export async function downloadLink(url: string, filename?: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename ?? extractFilename(url);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  } catch {
    openLink(url);
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const CLOSED_VIEWER: SdsViewerState = { open: false, url: null, title: '' };

export function useSdsDocuments(): UseSdsDocumentsReturn {
  const [viewer, setViewer] = useState<SdsViewerState>(CLOSED_VIEWER);
  const openViewer = (url: string | null | undefined, title = 'Document'): void => {
    if (!url) return;
    setViewer({ open: true, url, title });
  };
  const closeViewer = (): void => setViewer(CLOSED_VIEWER);
  return { openViewer, closeViewer, viewer, openLink, downloadLink };
}

// ─── Viewer dialog ─────────────────────────────────────────────────────────────

interface SdsDocumentViewerDialogProps {
  readonly viewer: SdsViewerState;
  readonly onClose: () => void;
}

export function SdsDocumentViewerDialog({ viewer, onClose }: SdsDocumentViewerDialogProps): JSX.Element | null {
  if (!viewer.open || !viewer.url) return null;

  const isPdf = viewer.url.toLowerCase().includes('.pdf') || viewer.url.toLowerCase().includes('pdf');

  return (
    <Dialog
      open={viewer.open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {viewer.title}
        </Typography>
        <Tooltip title="Open in new tab">
          <IconButton size="small" onClick={() => openLink(viewer.url!)}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton size="small" onClick={() => downloadLink(viewer.url!, viewer.title)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {isPdf ? (
          <Box
            component="iframe"
            src={viewer.url}
            title={viewer.title}
            sx={{ flex: 1, border: 'none', width: '100%', height: '100%' }}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              p: 4,
            }}
          >
            <Typography color="text.secondary">This file cannot be previewed inline.</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={() => openLink(viewer.url!)}>
                Open in new tab
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadLink(viewer.url!, viewer.title)}>
                Download
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
